/**
 * objectDragSource — make a module-pane list item draggable into the chat
 * composer (issue #1457).
 *
 * Returns the minimal `{ draggable, onDragStart }` props that set a JSON
 * `{ id, type, title }` payload on the drag `dataTransfer` under the chat's
 * vendor MIME (plus a `text/plain` fallback). The chat input's drop handler
 * (`useChatAttachments.handleChatDrop`) reads it back and splices a mention
 * marker in. Kept dependency-light and framework-agnostic so each module's
 * change stays a one-line spread.
 *
 * NOTE: this is HTML5 native drag. On surfaces that ALSO use dnd-kit pointer
 * drag (kanban reorder), attach these props to the INNER content element (a
 * child of the dnd-kit listener wrapper), never to the wrapper itself, so the
 * two drag systems don't fight over the same node.
 */

/** The vendor `dataTransfer` MIME for a dragged workspace-object reference.
 *  Duplicated from `features/chat/utils/mention-parser` so module code (which
 *  must not import chat internals) stays decoupled — both sides agree on the
 *  string. */
export const OBJECT_DRAG_MIME = 'application/x-xavior-object';

export interface DraggableObjectRef {
  id: string;
  type: string;
  title: string;
}

export interface ObjectDragSourceProps {
  draggable: true;
  onDragStart: (e: React.DragEvent) => void;
}

/**
 * Build the `{ draggable, onDragStart }` props for a draggable object row.
 * Spread onto a list item: `<div {...objectDragSource({ id, type, title })}>`.
 */
export function objectDragSource(ref: DraggableObjectRef): ObjectDragSourceProps {
  return {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      const payload = JSON.stringify({
        id: ref.id,
        type: ref.type,
        // Fall back to id for a blank/missing title (e.g. an unnamed contact).
        title: ref.title && ref.title.trim() ? ref.title : ref.id,
      });
      try {
        e.dataTransfer.setData(OBJECT_DRAG_MIME, payload);
        // text/plain fallback so a drop target that only reads plain text (and
        // browsers that normalize custom MIMEs) still gets the reference.
        e.dataTransfer.setData('text/plain', payload);
        e.dataTransfer.effectAllowed = 'copyMove';
      } catch {
        // dataTransfer is unavailable in some synthetic environments — no-op.
      }
    },
  };
}
